import React, { useState, useEffect } from 'react';
import { fetchWorkingAreas, signupUser } from '../services/api';

function SignUp({ onSignUpSuccess }) {
  const [form, setForm] = useState({
    name: '',
    username: '',
    phone: '',
    password: '',
    rePassword: '',
    groupId: '',
    photo: null
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [workingAreas, setWorkingAreas] = useState([]);
  const [loadingWorkingAreas, setLoadingWorkingAreas] = useState(true);

  // Fetch working areas on component mount
  useEffect(() => {
    const getWorkingAreas = async () => {
      try {
        setLoadingWorkingAreas(true);
        // Use locationID 1 for Renukut
        const locationID = 1;
        const result = await fetchWorkingAreas(locationID);
        if (result && result.workingAreas) {
          setWorkingAreas(result.workingAreas);
          console.log(`Loaded ${result.workingAreas.length} working areas for LocationID ${result.locationID}`);
        }
      } catch (err) {
        console.error('Failed to load working areas:', err);
      } finally {
        setLoadingWorkingAreas(false);
      }
    };

    getWorkingAreas();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo') {
      setForm({ ...form, photo: files[0] });
      setPhotoPreview(URL.createObjectURL(files[0]));
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (form.password !== form.rePassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Handle photo if selected
      let photoBase64 = null;
      if (form.photo) {
        photoBase64 = await convertFileToBase64(form.photo);
      }
      
      console.log('Attempting to sign up user:', {
        name: form.name,
        username: form.username,
        phone: form.phone,
        groupId: form.groupId,
        photoProvided: !!photoBase64
      });

      // Make sure the backend server is running
      try {
        const data = await signupUser({
          name: form.name,
          username: form.username,
          phone: form.phone,
          password: form.password,
          groupId: form.groupId,
          photo: photoBase64
        });
        console.log('Signup successful:', data);
        
        if (onSignUpSuccess) onSignUpSuccess();
      } catch (networkErr) {
        console.error('Network error during signup:', networkErr);
        
        if (networkErr.message.includes('Failed to fetch')) {
          setError('Connection to server failed. Please make sure the server is running and try again.');
        } else {
          setError(networkErr.message || 'Failed to register. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error during signup process:', err);
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract the base64 data part (remove data:image/xyz;base64, prefix)
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Background */}
      <img src="/loginp2.jpeg" alt="Signup Background" className="absolute inset-0 w-full h-full object-cover z-0" />
      <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/10 z-10" />
      {/* Top-left logo */}
      <div className="absolute top-0 left-0 z-20 pt-0 pl-8 md:pt-0 md:pl-12 lg:pt-2 lg:pl-16">
        <img src="/Hindalco_Logo.svg.png" alt="Hindalco Logo" className="w-20 md:w-24 lg:w-28" />
      </div>
      {/* Uplifted Welcome text */}
      <div className="absolute left-0 z-20 mt-4 md:mt-10 lg:mt-24 ml-8 md:ml-12 lg:ml-16" style={{maxWidth: '50vw', top: '80px'}}>
        <h2 className="text-4xl md:text-5xl  text-orange-400 mb-2 drop-shadow-lg">Hey There!</h2>
        <h1 className="text-7xl md:text-8xl  text-orange-400 drop-shadow-lg leading-none">Welcome</h1>
      </div>
      {/* Signup Card */}
      <div className="fixed top-4 bottom-4 right-0 flex items-center z-30 mr-2">
        <div className="h-[99vh] w-[440px] max-w-full bg-white bg-opacity-95 rounded-3xl shadow-3xl flex flex-col justify-center px-6 py-6">
          <div className="w-full flex flex-col flex-1">
            <img src="/DRIVE360.png" alt="DRIVE 360 Logo" className="w-56 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide font-poppins text-left">User Sign Up</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input name="name" type="text" value={form.name} onChange={handleChange} className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base placeholder-gray-400" placeholder="Name" required />
              <input name="username" type="text" value={form.username} onChange={handleChange} className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base placeholder-gray-400" placeholder="Username" required />
              <input name="phone" type="tel" value={form.phone} onChange={handleChange} className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base placeholder-gray-400" placeholder="Phone Number" required />
              <input name="location" type="text" value="Renukut" disabled className="w-full px-4 py-2 bg-gray-200 rounded-lg border border-gray-300 text-gray-600 outline-none text-base" placeholder="Location" />
              
              <select 
                name="groupId" 
                value={form.groupId} 
                onChange={handleChange} 
                className={`w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base ${!form.groupId ? 'text-gray-500' : 'text-gray-900'}`}
                required
                disabled={loadingWorkingAreas}
              >
                {loadingWorkingAreas ? (
                  <option value="">Loading working areas...</option>
                ) : (
                  <>
                    <option value="" disabled selected={!form.groupId}>Select working area</option>
                    {workingAreas.map(area => (
                      <option key={area.GroupID} value={area.GroupID}>
                        {area.WorkingAreaName}
                      </option>
                    ))}
                  </>
                )}
              </select>
              
              <div className="flex gap-2 items-center">
                <input name="password" type="password" value={form.password} onChange={handleChange} className="w-48 px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base placeholder-gray-400" placeholder="Password" required />
                <div className="flex items-center gap-2 ml-2">
                  <label className="flex items-center cursor-pointer">
                    <span className="mr-2 text-gray-700">Photo</span>
                    <input name="photo" type="file" accept="image/*" onChange={handleChange} className="hidden" />
                    <span className="inline-block bg-gray-200 rounded-lg px-3 py-1 text-sm text-gray-600 cursor-pointer">Upload</span>
                  </label>
                  {photoPreview && <img src={photoPreview} alt="Preview" className="w-10 h-10 rounded-full object-cover border-2 border-orange-400" />}
                </div>
              </div>
              <input name="rePassword" type="password" value={form.rePassword} onChange={handleChange} className="w-48 px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none text-base placeholder-gray-400 ml-0" placeholder="Re-Enter Password" required />
              {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-200 text-center">{error}</div>}
              <div className="flex gap-4 pt-1">
                <button type="button" className="flex-1 text-base font-semibold py-2 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-100 text-gray-800 transition duration-300 shadow-md" onClick={() => window.location.href = '/'}>Login</button>
                <button type="submit" disabled={isLoading || loadingWorkingAreas} className={`flex-1 text-base font-semibold py-2 rounded-lg transition duration-300 flex items-center justify-center ${isLoading || loadingWorkingAreas ? 'bg-orange-300 text-white cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md'}`}>{isLoading ? 'Signing up...' : 'Sign Up'}</button>
              </div>
            </form>
          </div>
          <div className="w-full mx-auto mt-4 pt-2 border-t border-gray-200 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Powered by:</span>
              <img src="/Eframe.png" alt="Eframe Logo" className="h-8" />
            </div>
            <div className="text-right">
              <p className="text-gray-800 font-medium">Eframe Infomedia Pvt Ltd</p>
              <a href="https://www.eframe.in/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-orange-500 hover:underline transition-colors">www.eframe.in</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUp; 